// lib/planCatalog.js  (BL-038 Story 38.2 — Plan Selection & Comparison)
//
// The single DISPLAY source for the conversion funnel's plan presentation:
// per-tier label / price / tagline, plus a capability comparison matrix.
//
// It is display metadata only — it NEVER decides entitlement. Tier identity,
// capability gating, and limits live in the authoritative entitlement catalog
// (lib/entitlements.js, the BL-041/ADR-050 single source). This module reads
// CANONICAL_TIERS / TIER_RANK / CAPABILITIES from there so the funnel can never
// drift from, or contradict, the engine — and never reintroduces the stale
// 'pro' label. No billing, no Stripe, no price IDs (pricing copy is reference
// text; actual prices are a future billing concern, kept out of scope here).

import { CANONICAL_TIERS, TIER_RANK, CAPABILITIES } from "./entitlements";

// Reference display copy per canonical tier. Prices are informational (the page
// is pre-launch / waitlist-only); they are NOT wired to any charge path.
export const PLAN_CATALOG = Object.freeze([
  Object.freeze({
    tier: "free",
    label: "Free",
    price: "$0",
    period: "entry tier",
    tagline:
      "A free entry point into the investor workspace to evaluate properties and see what the paid plans unlock.",
  }),
  Object.freeze({
    tier: "investor",
    label: "Investor",
    price: "$49",
    period: "per month",
    popular: true,
    tagline:
      "Everything you need to underwrite RV and outdoor hospitality deals, save your analyses, and move from research to decision.",
  }),
  Object.freeze({
    tier: "professional",
    label: "Professional",
    price: "$99",
    period: "per month",
    tagline:
      "For teams and active investors who need lease-leverage intelligence, priority support, API access, and bulk analysis.",
  }),
  Object.freeze({
    tier: "enterprise",
    label: "Enterprise",
    price: "Custom",
    period: "",
    tagline:
      "Custom integrations, dedicated account management, and tailored contracts for high-volume pipelines and multi-seat teams.",
  }),
]);

export function getPlan(tier) {
  return PLAN_CATALOG.find((p) => p.tier === tier) || null;
}

// Plan-intent whitelist — a plan interest is valid iff it is a canonical tier
// (never 'pro'). Mirrors the backend capture whitelist.
export function isValidPlanInterest(tier) {
  return CANONICAL_TIERS.includes(tier);
}

// Human labels for the capability comparison rows (entitlement communication).
const CAPABILITY_LABELS = Object.freeze({
  deal_analyzer: "Deal analyzer",
  lease_leverage: "Lease-leverage intelligence",
  discovery_engine: "Discovery engine",
  pdf_export: "PDF exports",
  ai_assistant: "AI assistant",
});

// Build the plan-comparison matrix straight from the entitlement catalog: a row
// per capability, with availability per canonical tier derived from each
// capability's minTier (active-grant view). This communicates which plan
// unlocks which capability without duplicating the catalog.
export function buildComparisonRows() {
  return Object.entries(CAPABILITIES).map(([key, def]) => ({
    key,
    label: CAPABILITY_LABELS[key] || key,
    availability: Object.fromEntries(
      CANONICAL_TIERS.map((tier) => [tier, TIER_RANK[tier] >= TIER_RANK[def.minTier]])
    ),
  }));
}
